import { Flex, Spin } from 'antd';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Environment } from 'relay-runtime';

import CalendarTable from '../calendarTable/calendarTable';
import AlertEmptyProject from '../components/alertEmptyProject';
import { BORDER_MAIN } from '../const';
import { getMappingIdIndex } from '../gantTransformData';
import useCommitTask from '../hooks/useCommitTask';
import useObserverComponent from '../hooks/useObserverComponent';
import { useSyncCalendarDate } from '../hooks/useSyncCalendarDate';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { CalendarScaleType, CalendarViewType, ItemTaskType, ItemType, OnTasksImport } from '../types';
import ButtonNavigation from './buttonNavigation';
import ButtonScale from './buttonScale';
import ButtonToday from './buttonToday';
import CalendarAgenda from './calendarAgenda';
import CalendarCarousel from './calendarCarousel';
import CalendarContainer from './calendarContainer';
import CalendarSearch from './calendarSearch';
import CalendarTableContainer from './calendarTableContainer';

export interface ICalendar {
    inputItems?: ItemType[];
    tasks?: ItemTaskType[];
    paddingBottom?: number;
    environment?: Environment;
    activePage?: CalendarViewType;
    scheduleScale?: CalendarScaleType;
    newTask?: ItemTaskType;
    authorId: string;
    onFinishLoadData?: () => void;
    onTasksImport?: OnTasksImport;
}

const Calendar: React.FC<ICalendar> = ({
    inputItems = [],
    tasks,
    activePage,
    scheduleScale,
    newTask,
    paddingBottom = 0,
    onTasksImport,
}) => {
    const dispatch = useDispatch<ComponentsDispatch>();
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const spinner = useSelector<RootState, boolean>((state) => state.spinner.spinner);
    const wasChangedTask = useSelector<RootState, boolean>((state) => state.calendarTasks.wasChangedTask);
    const { isVisible, ref } = useObserverComponent();
    const { addTaskToDataBase } = useCommitTask('day');

    useEffect(() => {
        if (newTask) {
            addTaskToDataBase(newTask);
        }
    }, [newTask]);

    useEffect(() => {
        dispatch(setterCalendar({ viewCalendar: activePage, scheduleScale }));
    }, [activePage]);

    useEffect(() => {
        if (!isVisible) {
            dispatch(setterCalendar({ currentTask: null }));
        }
    }, [isVisible]);

    useEffect(() => {
        const newMappingScheduleItemIdIndex = getMappingIdIndex(inputItems);

        dispatch(
            setterCalendar({
                items: [...inputItems],
                mappingItemIdIndex: newMappingScheduleItemIdIndex,
            }),
        );
    }, [inputItems]);

    useEffect(() => {
        if (wasChangedTask) {
            return;
        }

        dispatch(
            setterCalendar({
                tasks: tasks?.length ? tasks : [],
            }),
        );
    }, [tasks]);

    useEffect(() => {
        dispatch(setterCalendar({ paddingBottom }));
    }, [paddingBottom]);

    useSyncCalendarDate();

    return (
        // @ts-ignore
        <div ref={ref} style={{ userSelect: 'none' }}>
            {activePage !== 'table' ? (
                <>
                    <Flex justify={'space-between'} style={{ padding: '5px 0', borderBottom: BORDER_MAIN }}>
                        <CalendarSearch />
                        <ButtonNavigation />
                        <Flex align={'center'} justify={'flex-end'} gap={20}>
                            <ButtonToday />
                            <ButtonScale />
                        </Flex>
                    </Flex>
                    <CalendarContainer>
                        {!!filterProject.length ? (
                            <Spin tip={'Saving ...'} spinning={spinner} style={{ width: '100%' }}>
                                {isVisible && activePage === 'schedule' && <CalendarCarousel />}
                                {isVisible && activePage === 'agenda' && <CalendarAgenda />}
                            </Spin>
                        ) : (
                            <AlertEmptyProject />
                        )}
                    </CalendarContainer>
                </>
            ) : (
                <CalendarTableContainer>
                    <Spin tip={'Saving ...'} spinning={spinner} style={{ width: '100%' }}>
                        {isVisible && activePage === 'table' && <CalendarTable onTasksImport={onTasksImport} />}
                    </Spin>
                </CalendarTableContainer>
            )}
        </div>
    );
};

export default Calendar;
